import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, RotateCcw } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";

import { reabrirPedido } from "./api";

const brl = (v) => `R$ ${(Number(v) || 0).toFixed(2).replace(".", ",")}`;

function Confirmacao({ info, mesa, setMesa }) {
  const mesaTxt = info.tipo === "mesa";
  return (
    <div className="space-y-3 text-sm">
      <p>
        {mesaTxt
          ? "A mesa volta para a tela de Mesas aguardando pagamento, com os pagamentos já feitos. Lá você apaga o pagamento errado (ele sai do caixa na hora), lança o certo, dá desconto se precisar e fecha de novo."
          : "O pedido volta para o Delivery. A venda sai do caixa agora e entra de novo, com a forma certa, quando você concluir."}
      </p>
      {info.pagamentos?.length > 0 && (
        <div className="rounded-md border p-2 space-y-0.5">
          <p className="text-[11px] text-muted-foreground">Pagamentos lançados no caixa</p>
          {info.pagamentos.map((p, i) => (
            <p key={i} className="flex justify-between text-xs"><span>{p.forma}{p.travado ? " (online — não se apaga)" : ""}</span><span className="font-mono">{brl(p.valor)}</span></p>
          ))}
        </div>
      )}
      {info.mesa_ocupada && (
        <label className="block space-y-1">
          <span className="text-xs font-medium text-amber-700 dark:text-amber-400">
            {info.mesa ? `A Mesa ${info.mesa.numero} está ocupada.` : "A mesa deste pedido não existe mais."} Em qual mesa livre reabrir?
          </span>
          <select value={mesa} onChange={(e) => setMesa(e.target.value)} data-e2e="reabrir-mesa-destino"
            className="w-full h-9 rounded-md border bg-background px-2 text-sm">
            <option value="">Escolha a mesa</option>
            {info.mesas_livres.map((m) => <option key={m.id} value={m.id}>{m.nome || `Mesa ${m.numero}`}</option>)}
          </select>
        </label>
      )}
    </div>
  );
}

/** Botão "Reabrir pedido" do detalhe (Vendas → Pedidos) com a confirmação.
 *  Some para quem não tem a permissão; fica cinza com o motivo quando o
 *  servidor diz que não dá (caixa fechado, iFood, Balcão...). */
export default function ReabrirPedido({ pedido, info, onReaberto }) {
  const [aberto, setAberto] = useState(false);
  const [mesa, setMesa] = useState("");
  const [enviando, setEnviando] = useState(false);
  const navigate = useNavigate();
  if (!info?.permitido || pedido?.status !== "concluido") return null;
  if (!info.pode) {
    return (
      <div className="flex-1 min-w-0" data-e2e="reabrir-indisponivel">
        <Button variant="outline" size="sm" className="w-full h-9 text-xs" disabled><RotateCcw className="w-3.5 h-3.5 mr-1" />Reabrir pedido</Button>
        <p className="text-[10px] text-muted-foreground mt-1 leading-tight">{info.motivo}</p>
      </div>
    );
  }
  const confirmar = async () => {
    if (info.mesa_ocupada && !mesa) { toast.error("Escolha a mesa livre."); return; }
    setEnviando(true);
    try {
      const r = await reabrirPedido(pedido.id, info.mesa_ocupada ? mesa : null);
      setAberto(false);
      const naMesa = r.tipo === "mesa";
      toast.success(naMesa ? `Mesa ${r.mesa_numero} reaberta: está em Mesas aguardando pagamento.`
        : `Pedido reaberto: está no Delivery, em ${r.status === "pronto" ? "Pronto" : "Saiu para entrega"}.`, {
        duration: 10000,
        action: { label: naMesa ? "Ir para Mesas" : "Ir para o Delivery", onClick: () => navigate(naMesa ? "/admin/mesas" : "/admin/delivery") },
      });
      onReaberto?.(r);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Não consegui reabrir o pedido.", { duration: 8000 });
    } finally {
      setEnviando(false);
    }
  };
  return (
    <>
      <Button variant="outline" size="sm" className="flex-1 h-9 text-xs border-amber-400 text-amber-700 hover:bg-amber-50 dark:text-amber-400 dark:hover:bg-amber-950/30"
        onClick={() => { setMesa(""); setAberto(true); }} data-e2e="reabrir-pedido">
        <RotateCcw className="w-3.5 h-3.5 mr-1" />Reabrir pedido
      </Button>
      <Dialog open={aberto} onOpenChange={(v) => !enviando && setAberto(v)}>
        <DialogContent className="max-w-md" data-e2e="reabrir-dialog">
          <DialogTitle>Reabrir pedido {pedido.codigo}?</DialogTitle>
          <DialogDescription className="sr-only">Confirmação da reabertura do pedido</DialogDescription>
          <Confirmacao info={info} mesa={mesa} setMesa={setMesa} />
          <div className="flex gap-2 pt-2">
            <Button variant="ghost" className="flex-1" onClick={() => setAberto(false)} disabled={enviando}>Voltar</Button>
            <Button className="flex-1" onClick={confirmar} disabled={enviando || (info.mesa_ocupada && !mesa)} data-e2e="reabrir-confirmar">
              {enviando ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <RotateCcw className="w-4 h-4 mr-1" />}Reabrir
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
